import { useMemo, useCallback } from 'react';
import { Role } from '@/enums/Role';
import { useAuthStore } from '@/store';
const hasAccessRoles = [Role.KASMACCESS.toString(), Role.KASMADMIN.toString()];
const usePermissions = (ownerHubspotId) => {
    const displayName = useAuthStore(s => s.name || s.email || '');
    const hubspotIds = useAuthStore(s => s.user_metadata?.owner_ids);
    const isKasmAdmin = useAuthStore(s => s.roles?.includes(Role.KASMADMIN));
    const hasKasmAccess = useAuthStore(s => s.roles?.some(role => hasAccessRoles.includes(role)));
    const isSuperAdmin = useMemo(() => {
        return !!isKasmAdmin && !hubspotIds?.length;
    }, [isKasmAdmin, hubspotIds?.length]);
    const displayRole = useMemo(() => {
        return isSuperAdmin ? 'Kasm Admin' : isKasmAdmin ? 'Kasm User' : 'User';
    }, [isKasmAdmin, isSuperAdmin]);
    const canAccessOwner = useCallback((hubspotId) => {
        if (!hasKasmAccess)
            return false;
        if (isSuperAdmin)
            return true;
        return !!hubspotId && !!hubspotIds?.includes(hubspotId);
    }, [isSuperAdmin, hubspotIds, hasKasmAccess]);
    const hasOwnerAccess = useMemo(() => {
        return canAccessOwner(ownerHubspotId);
    }, [canAccessOwner, ownerHubspotId]);
    const canCreate = useCallback((action, owner, company) => {
        if (!hasKasmAccess)
            return false;
        if (!owner)
            return true;
        if (!canAccessOwner(owner.hubspotId))
            return false;
        if (owner.status !== 'active')
            return false;
        if (action === 'license' && !owner.canCreateLicenses)
            return false;
        if (action === 'company' && !owner.canCreateCompanies)
            return false;
        if (action === 'license' && company && company.status !== 'active')
            return false;
        return true;
    }, [canAccessOwner, hasKasmAccess]);
    return {
        canAccessOwner,
        canCreate,
        displayName,
        displayRole,
        hasOwnerAccess,
        isKasmAdmin,
        isSuperAdmin,
    };
};
export default usePermissions;
